/**
 * apiRequest — единая обёртка над fetch для всего фронта.
 *
 * Обрабатывает backend-контракт (_admin/documentation/backend-frontend-contract.md):
 *  - CSRF-токен из <meta name="csrf-token"> автоматически
 *  - Accept: application/json + X-Requested-With: XMLHttpRequest → бэк отдаёт централизованный формат ошибок
 *  - Распаковывает { error: { code, message, details } } и бросает APIError
 *  - 419 → CSRFTokenMismatchError (подсказка перезагрузить страницу)
 *  - 429 → RateLimitError с Retry-After
 *  - Сетевые ошибки → NetworkError
 *
 * На уровне вызывающего кода:
 *  - success → возвращается распарсенный JSON (или null для 204)
 *  - error → брошено исключение с .code, .message, .details, .status
 *
 * Вызывающий сам решает как показывать (toast/inline/modal). Стандартный путь:
 *   try { const data = await apiRequest('/api/x', {method:'POST', body:{...}}); }
 *   catch (e) { showToast('error', e.message); }
 */

export class APIError extends Error {
    constructor({ code, message, details, status }) {
        super(message);
        this.name = 'APIError';
        this.code = code;
        this.details = details || null;
        this.status = status;
    }
}

export class CSRFTokenMismatchError extends APIError {
    constructor() {
        super({
            code: 'CSRF_TOKEN_MISMATCH',
            message: 'Session expired. Please reload the page.',
            status: 419,
        });
        this.name = 'CSRFTokenMismatchError';
    }
}

export class RateLimitError extends APIError {
    constructor(retryAfterSeconds) {
        const retryMsg = retryAfterSeconds
            ? ` Please wait ${retryAfterSeconds} seconds.`
            : ' Please try again later.';
        super({
            code: 'RATE_LIMITED',
            message: `Too many requests.${retryMsg}`,
            status: 429,
        });
        this.name = 'RateLimitError';
        this.retryAfter = retryAfterSeconds;
    }
}

export class NetworkError extends APIError {
    constructor(cause) {
        super({
            code: 'NETWORK_ERROR',
            message: 'Network error. Please check your connection.',
            status: 0,
        });
        this.name = 'NetworkError';
        this.cause = cause;
    }
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

/**
 * @param {string} url
 * @param {object} options
 * @param {string} [options.method='GET'] GET|POST|PUT|PATCH|DELETE
 * @param {object|FormData|null} [options.body] JSON-сериализуется автоматически, кроме FormData
 * @param {object} [options.headers] доп. хедеры
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>} распарсенный JSON ответ или null для 204
 */
export async function apiRequest(url, { method = 'GET', body = null, headers = {}, signal } = {}) {
    const init = {
        method,
        headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken(),
            ...headers,
        },
        signal,
    };

    if (body !== null && body !== undefined) {
        if (body instanceof FormData) {
            init.body = body;
        } else {
            init.headers['Content-Type'] = 'application/json';
            init.body = JSON.stringify(body);
        }
    }

    let response;
    try {
        response = await fetch(url, init);
    } catch (e) {
        if (e.name === 'AbortError') throw e;
        throw new NetworkError(e);
    }

    if (response.status === 204) return null;
    if (response.status === 419) throw new CSRFTokenMismatchError();
    if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10) || null;
        throw new RateLimitError(retryAfter);
    }

    const contentType = response.headers.get('Content-Type') || '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json().catch(() => null) : await response.text();

    if (!response.ok) {
        // Централизованный бэкенд-формат: { error: { code, message, details } }
        if (payload && typeof payload === 'object' && payload.error) {
            throw new APIError({ ...payload.error, status: response.status });
        }
        throw new APIError({
            code: 'HTTP_ERROR',
            message: typeof payload === 'string' && payload ? payload : response.statusText,
            status: response.status,
        });
    }

    return payload;
}
